// Formulário do cadastro de um estabelecimento da transportadora (PO1-2308).
// O MESMO componente atende o MeuFrete (matriz e filiais), o card Cadastro do
// painel de habilitação e o detalhe admin da Everlog: CNPJ e razão social só
// leitura (a razão social muda só por "Atualizar pela Receita"); fantasia,
// endereço e telefone da empresa editáveis. Quem salva é quem chama
// (onSalvar / onReceita), porque a API muda conforme quem edita.
//
// Logo (PO1-2722): o bloco "Identidade visual" aparece quando o chamador passa
// onEnviarLogo (e onRemoverLogo). É ação INDEPENDENTE do botão Salvar — o
// salvar() aborta com "Nada mudou." quando nenhum campo de texto mudou, então o
// logo nunca poderia ir junto. O form valida e reduz a imagem (PNG em até 640×320, sem cortar) e
// entrega o Blob pronto; quem chama escolhe a API (transportadora ou admin) e
// recarrega o estabelecimento, que volta com `logoUrl` novo.
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import TransportadoraMonogram from "views/Empresa/Transportadoras/TransportadoraMonogram";
import api from "services/api";
import { LOGO_ACCEPT, LOGO_LEGENDA, validarArquivoLogo, prepararLogo } from "services/transportadoraLogoApi";
import { formatDocument, formatCep } from "utils/formatDoc";

const digits = (v) => String(v || "").replace(/\D/g, "");
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };
const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const readonlyStyle = { background: AUTOPILOT_COLORS.insetSurface };
const logoGhostButtonStyle = { background: "transparent", borderColor: AUTOPILOT_COLORS.borderFaint, color: AUTOPILOT_COLORS.text, fontWeight: 600 };
const logoLinkButtonStyle = { background: "transparent", border: "none", color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", textDecoration: "underline", padding: "4px 6px", cursor: "pointer" };

// PO1-3743: o CEP vem numérico do banco e perde o zero à esquerda (01311300 chega
// como 1311300). Completa para 8 dígitos e formata, senão o campo mostra um CEP
// "faltando dígito" e o usuário tenta corrigir o que já está certo.
export function cepDoBanco(v) {
  const d = digits(v);
  return d && d.length < 8 ? formatCep(d.padStart(8, "0")) : formatCep(d);
}

export function formDoEstabelecimento(e) {
  return {
    fantasia: (e && e.fantasia) || "",
    cep: e && e.cep ? cepDoBanco(e.cep) : "",
    logradouro: (e && e.logradouro) || "",
    numero: e && e.numero !== null && e.numero !== undefined ? String(e.numero) : "",
    complemento: (e && e.complemento) || "",
    bairro: (e && e.bairro) || "",
    dddTelefone: (e && e.dddTelefone) || "",
    telefone: (e && e.telefone) || "",
  };
}

// Só os campos que mudaram viram o corpo do PUT.
export function diffDoForm(form, estabelecimento) {
  const base = formDoEstabelecimento(estabelecimento);
  const saida = {};
  Object.keys(form).forEach((k) => {
    const atual = k === "cep" ? digits(base[k]) : String(base[k] || "").trim();
    const novo = k === "cep" ? digits(form[k]) : String(form[k] || "").trim();
    if (atual !== novo) saida[k] = form[k];
  });
  return saida;
}

// PO1-3737: texto do aviso do "Atualizar pela Receita", que agora pode trazer
// o endereço (cadastro com o CEP-coringa) além da razão social.
const NOMES_CAMPO_RECEITA = { razaoSocial: "razão social", fantasiaSefaz: "nome fantasia", cep: "CEP", logradouro: "logradouro", numero: "número", complemento: "complemento", bairro: "bairro" };
export function resumoReceita(out) {
  const alterados = (out && out.alterados) || [];
  if (!alterados.length) return "Nada mudou: o cadastro já estava igual ao da Receita.";
  return `Atualizado pela Receita: ${alterados.map((c) => NOMES_CAMPO_RECEITA[c] || c).join(", ")}.`;
}

// PO1-3737: campo que o usuário não mexeu (igual à base anterior) segue o
// banco; campo editado e não salvo fica como o usuário deixou.
export function mesclarComBanco(form, baseAnterior, novaBase) {
  const saida = {};
  Object.keys(novaBase).forEach((k) => {
    const igual = k === "cep" ? digits(form[k]) === digits(baseAnterior[k]) : String(form[k] || "") === String(baseAnterior[k] || "");
    saida[k] = igual ? novaBase[k] : form[k];
  });
  return saida;
}

export default function CadastroEstabelecimentoForm({
  estabelecimento,
  onSalvar,
  onReceita,
  salvando = false,
  podeReceita = true,
  compact = false,
  onCancelar,
  onEnviarLogo,
  onRemoverLogo,
  logoBusy = false,
}) {
  const [form, setForm] = useState(() => formDoEstabelecimento(estabelecimento));
  const [cepInfo, setCepInfo] = useState(null);
  const [cepErro, setCepErro] = useState("");
  const [consultandoCep, setConsultandoCep] = useState(false);
  const [receitaBusy, setReceitaBusy] = useState(false);
  const [erros, setErros] = useState({});
  const [logoErro, setLogoErro] = useState("");
  const [preparandoLogo, setPreparandoLogo] = useState(false);
  const logoInputRef = useRef(null);

  // PO1-3737: o chamador recarrega o estabelecimento depois de outras ações
  // (logo, Receita, conector na aba vizinha). Recarregar NÃO pode descartar o
  // que foi digitado e não salvo: só os campos que o usuário não mexeu
  // acompanham o banco. Troca de estabelecimento (outro id) reinicia tudo.
  const baseAnterior = useRef({ id: estabelecimento && estabelecimento.id, form: formDoEstabelecimento(estabelecimento) });
  useEffect(() => {
    const novaBase = formDoEstabelecimento(estabelecimento);
    const anterior = baseAnterior.current;
    const mesmoCadastro = anterior.id === (estabelecimento && estabelecimento.id);
    baseAnterior.current = { id: estabelecimento && estabelecimento.id, form: novaBase };
    if (mesmoCadastro) {
      // `form` é o desta renderização (o effect roda logo depois dela).
      const mesclado = mesclarComBanco(form, anterior.form, novaBase);
      setForm(mesclado);
      if (digits(mesclado.cep) === digits(novaBase.cep)) {
        setCepInfo(estabelecimento && estabelecimento.cidade ? { cidade: estabelecimento.cidade, uf: estabelecimento.uf } : null);
      }
      return;
    }
    setForm(novaBase);
    setErros({});
    setCepErro("");
    setLogoErro("");
    setCepInfo(estabelecimento && estabelecimento.cidade ? { cidade: estabelecimento.cidade, uf: estabelecimento.uf } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estabelecimento]);
  const alteracoesPendentes = Object.keys(diffDoForm(form, estabelecimento)).length > 0;

  // Logo: valida, reduz no canvas e entrega o Blob a quem chama. O valor do
  // input é lido antes de qualquer await (React 16 devolve o evento ao pool).
  const escolherLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || !onEnviarLogo) return;
    const v = validarArquivoLogo(file);
    if (!v.valido) {
      setLogoErro(v.erro);
      return;
    }
    setLogoErro("");
    setPreparandoLogo(true);
    try {
      const { blob, nome } = await prepararLogo(file);
      await onEnviarLogo(blob, nome);
    } catch (err) {
      setLogoErro((err && err.message) || "Não foi possível preparar a imagem.");
    } finally {
      setPreparandoLogo(false);
    }
  };
  const logoOcupado = logoBusy || preparandoLogo || salvando;

  const set = (campo) => (e) => {
    const valor = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  // Consulta o CEP (cidade/UF) e, quando o preview traz, logradouro e bairro.
  const consultarCep = async () => {
    const cep = digits(form.cep);
    if (cep.length !== 8) {
      setCepErro("CEP deve ter 8 dígitos");
      return;
    }
    setConsultandoCep(true);
    setCepErro("");
    try {
      const { data } = await api.get(`/endereco/cep/${cep}`);
      setCepInfo({ cidade: data.cidade, uf: data.uf });
    } catch (err) {
      setCepInfo(null);
      setCepErro((err.response && err.response.data && err.response.data.error) || "CEP não encontrado");
    }
    try {
      const { data } = await api.get(`/endereco/cep/${cep}/preview`);
      setForm((f) => ({
        ...f,
        logradouro: f.logradouro || data.logradouro || "",
        bairro: f.bairro || data.bairro || "",
      }));
      if (data.cidade) setCepInfo({ cidade: data.cidade, uf: data.uf });
      setCepErro("");
    } catch (_) {
      // preview é best-effort
    } finally {
      setConsultandoCep(false);
    }
  };

  const validar = () => {
    const e = {};
    if (!String(form.fantasia || "").trim()) e.fantasia = "Informe o nome fantasia";
    if (String(form.fantasia || "").length > 100) e.fantasia = "Até 100 caracteres";
    if (form.cep && digits(form.cep).length !== 8) e.cep = "CEP deve ter 8 dígitos";
    if (form.numero !== "" && !/^\d+$/.test(String(form.numero))) e.numero = "Só números";
    if (digits(form.dddTelefone) && (digits(form.dddTelefone).length < 2 || digits(form.dddTelefone).length > 4)) e.dddTelefone = "DDD de 2 a 4 dígitos";
    if (digits(form.telefone) && (digits(form.telefone).length < 8 || digits(form.telefone).length > 20)) e.telefone = "Telefone de 8 a 20 dígitos";
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    const dados = diffDoForm(form, estabelecimento);
    if (!Object.keys(dados).length) {
      setErros({ geral: "Nada mudou." });
      return;
    }
    await onSalvar(dados);
  };

  const receita = async () => {
    if (!onReceita) return;
    setReceitaBusy(true);
    try {
      await onReceita();
    } finally {
      setReceitaBusy(false);
    }
  };

  if (!estabelecimento) return null;

  return (
    <div>
      {onEnviarLogo && (
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label style={labelStyle}>Identidade visual</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.9rem", flexWrap: "wrap" }}>
                <TransportadoraMonogram nome={estabelecimento.fantasia || estabelecimento.razaoSocial} size={compact ? 56 : 72} logoUrl={estabelecimento.logoUrl} />
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <Button type="button" size="sm" style={logoGhostButtonStyle} onClick={() => logoInputRef.current && logoInputRef.current.click()} disabled={logoOcupado}>
                      {logoOcupado ? <Spinner size="sm" /> : estabelecimento.logoUrl ? "Trocar logo" : "Adicionar logo"}
                    </Button>
                    {estabelecimento.logoUrl && onRemoverLogo && (
                      <button type="button" style={logoLinkButtonStyle} onClick={() => onRemoverLogo()} disabled={logoOcupado}>
                        Remover logo
                      </button>
                    )}
                    <input ref={logoInputRef} type="file" accept={LOGO_ACCEPT} style={{ display: "none" }} onChange={escolherLogo} />
                  </div>
                  <span style={hintStyle}>{LOGO_LEGENDA}. Vale para todos os embarcadores que veem esta transportadora.</span>
                  {logoErro && <span style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>{logoErro}</span>}
                </div>
              </div>
            </FormGroup>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={compact ? 6 : 4}>
          <FormGroup>
            <Label style={labelStyle}>CNPJ</Label>
            <Input value={formatDocument(estabelecimento.cnpjCpf)} readOnly style={readonlyStyle} title="O CNPJ não é editável" />
          </FormGroup>
        </Col>
        <Col md={compact ? 6 : 8}>
          <FormGroup>
            <Label style={labelStyle}>Razão social</Label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Input value={estabelecimento.razaoSocial || ""} readOnly style={readonlyStyle} title='A razão social só muda pelo botão "Atualizar pela Receita"' />
              {podeReceita && onReceita && (
                <Button color="info" outline size="sm" onClick={receita} disabled={receitaBusy || salvando} style={{ whiteSpace: "nowrap" }} title="Consulta a Receita Federal e atualiza só a razão social (1 vez a cada 10 min)">
                  {receitaBusy ? <Spinner size="sm" /> : "Atualizar pela Receita"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={compact ? 12 : 6}>
          <FormGroup>
            <Label style={labelStyle}>Nome fantasia *</Label>
            <Input value={form.fantasia} onChange={set("fantasia")} maxLength={100} invalid={!!erros.fantasia} />
            {erros.fantasia && <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>{erros.fantasia}</div>}
          </FormGroup>
        </Col>
        <Col md={compact ? 4 : 2}>
          <FormGroup>
            <Label style={labelStyle}>DDD</Label>
            <Input value={form.dddTelefone} onChange={set("dddTelefone")} maxLength={4} invalid={!!erros.dddTelefone} placeholder="47" />
          </FormGroup>
        </Col>
        <Col md={compact ? 8 : 4}>
          <FormGroup>
            <Label style={labelStyle}>Telefone da empresa</Label>
            <Input value={form.telefone} onChange={set("telefone")} maxLength={20} invalid={!!erros.telefone} placeholder="3333-4444" />
            {(erros.dddTelefone || erros.telefone) && <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>{erros.dddTelefone || erros.telefone}</div>}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={compact ? 6 : 3}>
          <FormGroup>
            <Label style={labelStyle}>CEP</Label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Input value={form.cep} onChange={set("cep")} onBlur={() => digits(form.cep).length === 8 && consultarCep()} maxLength={9} invalid={!!erros.cep || !!cepErro} placeholder="00000-000" />
              <Button color="info" outline size="sm" onClick={consultarCep} disabled={consultandoCep}>
                {consultandoCep ? <Spinner size="sm" /> : "Buscar"}
              </Button>
            </div>
            {cepErro && <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>{cepErro}</div>}
            {cepInfo && <div style={hintStyle}>{cepInfo.cidade} / {cepInfo.uf}</div>}
          </FormGroup>
        </Col>
        <Col md={compact ? 6 : 5}>
          <FormGroup>
            <Label style={labelStyle}>Logradouro</Label>
            <Input value={form.logradouro} onChange={set("logradouro")} maxLength={255} />
          </FormGroup>
        </Col>
        <Col md={compact ? 4 : 2}>
          <FormGroup>
            <Label style={labelStyle}>Número</Label>
            <Input value={form.numero} onChange={set("numero")} invalid={!!erros.numero} />
            {erros.numero && <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>{erros.numero}</div>}
          </FormGroup>
        </Col>
        <Col md={compact ? 8 : 2}>
          <FormGroup>
            <Label style={labelStyle}>Complemento</Label>
            <Input value={form.complemento} onChange={set("complemento")} maxLength={255} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={compact ? 12 : 4}>
          <FormGroup>
            <Label style={labelStyle}>Bairro</Label>
            <Input value={form.bairro} onChange={set("bairro")} maxLength={60} />
          </FormGroup>
        </Col>
      </Row>
      {erros.geral && <div style={{ ...hintStyle, marginBottom: "0.5rem" }}>{erros.geral}</div>}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <Button color="info" size="sm" onClick={salvar} disabled={salvando}>
          {salvando ? <Spinner size="sm" /> : "Salvar"}
        </Button>
        {onCancelar && (
          <Button color="secondary" outline size="sm" onClick={onCancelar} disabled={salvando}>
            Cancelar
          </Button>
        )}
        {alteracoesPendentes && !salvando && (
          <span style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600 }}>Alterações não salvas</span>
        )}
        <span style={hintStyle}>CEP atual: {(estabelecimento.cep && cepDoBanco(estabelecimento.cep)) || "não informado"}. Toda alteração fica no histórico.</span>
      </div>
    </div>
  );
}
